<template>
  <div class="exporter">
    <div
      class="exporter-head--title black--text font-weight-medium"
      :class="{ 'fs-18': breakpointXs, 'fs-25': !breakpointXs }"
    >
      <DashboardBreadcrumb :items="breadCrumbItems" />
    </div>
    <div class="exporter-body mt-4">
      <v-card elevation="4" class="px-8 py-6">
        <v-row
          class="loader fill-height py-6"
          align="center"
          justify="center"
          v-if="isExporterFormLoading || retrievingEditExportJob"
        >
          <v-progress-circular indeterminate color="primary"></v-progress-circular>
        </v-row>
        <div v-else>
          <div
            class="exporter-body--title fw-600 d-flex justify-space-between"
            :class="{ 'fs-18': breakpointXs, 'fs-25': !breakpointXs }"
          >
            <span>Exporter Details</span>
            <span>
              <v-tooltip top>
                <template v-slot:activator="{ on }">
                  <v-btn @click="makeFieldsNull" color="blue-grey" v-on="on">
                    <span class="font-weight-bold white--text">Reset</span>
                    <v-icon class="font-weight-bold white--text fs-15 ml-2">fas fa-undo</v-icon>
                  </v-btn>
                </template>
                {{ resetIconMessage() }}
              </v-tooltip>
            </span>
          </div>
          <div class="exporter-body--form pt-3">
            <ValidationObserver @keydown.tab="focusNext" v-slot="{ passes }">
              <v-row>
                <v-col cols="12" :xs="12" :sm="6" :md="6" :lg="6">
                  <AutoComplete
                    v-model="exporterName"
                    rules="max:50"
                    :loading="isExporterFormLoading || savingExportJob"
                    showIcons
                    :item-text="'name'"
                    :return-object="true"
                    :items="exporters"
                    label="Exporter Name"
                    :showOnlyErrorIcons="true"
                  />
                </v-col>
                <v-col cols="12" :xs="12" :sm="6" :md="6" :lg="6">
                  <TextField
                    v-model="iec"
                    rules="required|max:10"
                    showIcons
                    :showOnlyErrorIcons="true"
                    :loading="isExporterFormLoading || savingExportJob"
                    label="IEC"
                  />
                </v-col>
              </v-row>
              <v-row>
                <v-col cols="12" :xs="12" :sm="6" :md="6" :lg="6">
                  <TextField
                    v-model="address1"
                    :loading="isExporterFormLoading || savingExportJob"
                    rules="max:35"
                    showIcons
                    :showOnlyErrorIcons="true"
                    label="Address 1"
                  />
                </v-col>
                <v-col cols="12" :xs="12" :sm="6" :md="6" :lg="6">
                  <TextField
                    v-model="address2"
                    :loading="isExporterFormLoading || savingExportJob"
                    rules="max:35"
                    showIcons
                    :showOnlyErrorIcons="true"
                    label="Address 2"
                  />
                </v-col>
              </v-row>
              <v-row>
                <v-col cols="12" :xs="12" :sm="6" :md="6" :lg="6">
                  <TextField
                    v-model="branch"
                    :loading="isExporterFormLoading || savingExportJob"
                    rules="required|max:3"
                    showIcons
                    :showOnlyErrorIcons="true"
                    label="Branch"
                  />
                </v-col>
                <v-col cols="12" :xs="12" :sm="6" :md="6" :lg="6">
                  <AutoComplete
                    v-model="city"
                    :loading="isExporterFormLoading || savingExportJob"
                    showIcons
                    item-text="text"
                    :items="cities"
                    :showOnlyErrorIcons="true"
                    label="City"
                  />
                </v-col>
                <v-col cols="12" :xs="12" :sm="6" :md="6" :lg="6">
                  <AutoComplete
                    v-model="state"
                    :loading="isExporterFormLoading || savingExportJob"
                    rules="required"
                    :items="states"
                    item-text="text"
                    showIcons
                    :showOnlyErrorIcons="true"
                    label="State"
                  />
                </v-col>
                <v-col cols="12" :xs="12" :sm="6" :md="6" :lg="6">
                  <TextField
                    v-model="pin"
                    :loading="isExporterFormLoading || savingExportJob"
                    rules="max:6"
                    showIcons
                    :showOnlyErrorIcons="true"
                    label="Pin"
                  />
                </v-col>
              </v-row>
              <v-row>
                <v-col cols="12" :xs="12" :sm="6" :md="6" :lg="6">
                  <AutoComplete
                    v-model="authorizedDealerCode"
                    :loading="isExporterFormLoading || savingExportJob"
                    rules="required"
                    :items="adCodes"
                    item-text="adCodeWithBankName"
                    :return-object="true"
                    showIcons
                    :showOnlyErrorIcons="true"
                    label="Authorized Dealer Code"
                  />
                </v-col>

                <v-col cols="12" :xs="12" :sm="6" :md="6" :lg="6">
                  <TextField
                    v-model="bankName"
                    :loading="isExporterFormLoading || savingExportJob"
                    showIcons
                    :showOnlyErrorIcons="true"
                    label="Bank Name"
                  />
                </v-col>
                <v-col cols="12" :xs="12" :sm="6" :md="6" :lg="6">
                  <AutoComplete
                    v-model="gstnTypesArray"
                    :loading="isExporterFormLoading || savingExportJob"
                    rules="required"
                    :items="gstInTypes"
                    :item-text="'text'"
                    :return-object="true"
                    showIcons
                    :showOnlyErrorIcons="true"
                    label="GST Type"
                  />
                </v-col>
                <v-col cols="12" :xs="12" :sm="6" :md="6" :lg="6">
                  <TextField
                    v-model="gstnId"
                    :loading="isExporterFormLoading || savingExportJob"
                    rules="required|max:20"
                    showIcons
                    :showOnlyErrorIcons="true"
                    label="GST ID"
                  />
                </v-col>
              </v-row>
              <v-row>
                <v-col cols="12" :xs="12" :sm="6" :md="6" :lg="6">
                  <div class="d-sm-flex justify-start" :class="{ 'text-center': breakpointXs }">
                    <v-btn
                      width="140"
                      color="info"
                      large
                      class="white--text"
                      :loading="isExporterFormLoading || savingExportJob"
                      @click="$router.push({ name: 'dashboard.export.sbMain' })"
                    >
                      <v-icon class="font-weight-bold fs-15 mr-2">fa fa-arrow-circle-left</v-icon>
                      <span class="font-weight-bold fs-15">Previous</span>
                    </v-btn>
                  </div>
                </v-col>
                <v-col cols="12" :xs="12" :sm="6" :md="6" :lg="6">
                  <div class="d-sm-flex justify-end" :class="{ 'text-center': breakpointXs }">
                    <v-btn
                      width="140"
                      color="button-primary"
                      large
                      class="white--text"
                      :loading="isExporterFormLoading || savingExportJob"
                      @click="passes(saveExporter)"
                    >
                      <span class="font-weight-bold fs-15">Save</span>
                    </v-btn>
                    <div class="ml-2 mr-2 my-1"></div>
                    <v-btn
                      width="140"
                      color="info"
                      large
                      class="white--text"
                      :loading="isExporterFormLoading || savingExportJob"
                      @click="$router.push({ name: 'dashboard.export.consignee' })"
                    >
                      <span class="font-weight-bold fs-15">Next</span>
                      <v-icon class="font-weight-bold fs-15 ml-2">fa fa-arrow-circle-right</v-icon>
                    </v-btn>
                  </div>
                </v-col>
              </v-row>
            </ValidationObserver>
          </div>
        </div>
      </v-card>
    </div>
    <div class="exporter-body mt-8" :class="{ 'mx-8': !xsOrsm }"></div>
  </div>
</template>
<script>
import DashboardBreadcrumb from "@/modules/DashboardModule/components/DashboardBreadcrumb";
import { ValidationObserver } from "vee-validate";
import TextField from "@/components/TextField";
import AutoComplete from "@/components/AutoComplete";
import * as MT from "@/modules/DashboardModule/modules/ImportExportModule/modules/ExportModule/store/mutationTypes";
import handleExportJobMixin from "@/modules/DashboardModule/modules/ImportExportModule/mixins/handleExportJobMixin";
import commonMethodsMixin from "@/modules/commonMethodsMixin";
export default {
  name: "Exporter",
  components: {
    DashboardBreadcrumb,
    ValidationObserver,
    TextField,
    AutoComplete
  },
  data() {
    return {
      isExporterFormLoading: false,
      adCodes: []
    };
  },
  mixins: [commonMethodsMixin, handleExportJobMixin],
  methods: {
    saveExporter() {
      this.saveExportJob();
    },
    makeFieldsNull() {
      this.iec = null;
      this.address1 = null;
      this.address2 = null;
      this.branch = null;
      this.city = "";
      this.state = "";
      this.pin = null;
      this.gstnTypesArray = "";
      this.gstnId = "";
      this.bankName = null;
      this.authorizedDealerCode = "";
    },
    retrieveAdCodesFromExporterName() {
      // if exporter name is not null and adCodes array is empty
      // means it is an edit export job and is unable to retieve adCodes
      if (this.exporterName !== null && this.adCodes.length <= 0) {
        const exporterObj = this.exporters.find(exporter => exporter.name == this.exporterName);
        if (exporterObj !== undefined) {
          // again resetting to set adCodes
          this.exporterName = exporterObj;
        }
        return;
      }
    }
  },
  mounted() {
    this.retrieveAdCodesFromExporterName();
  },
  computed: {
    exporterName: {
      get() {
        return this.$store.state.ExportModuleStore.sbMain.exporterName;
      },
      set(value) {
        this.adCodes = value.ad_codes.map(adCode => {
          adCode.adCodeWithBankName = adCode.ad_code + " - " + adCode.bank_name;
          return adCode;
        });
        if (this.adCodes.length > 0 && this.authorizedDealerCode === null) {
          this.authorizedDealerCode = this.adCodes[0];
        }
        value.iec !== undefined ? (this.iec = value.iec) : null;
        value.address1 !== undefined ? (this.address1 = value.address1) : null;
        value.address2 !== undefined ? (this.address2 = value.address2) : null;
        value.branch !== undefined ? (this.branch = value.branch) : null;
        value.city !== undefined ? (this.city = { text: value.city }) : null;
        value.state !== undefined ? (this.state = value.state) : null;
        value.pin !== undefined ? (this.pin = value.pin) : null;
        value.gstnType !== undefined ? (this.gstnType = value.gstnType) : null;
        value.gstId !== undefined ? (this.gstnId = value.gstId) : null;
        this.$store.commit(MT.SET_SBMAIN_EXPORTER_NAME, { value: value.name });
      }
    },
    iec: {
      get() {
        return this.$store.state.ExportModuleStore.sbMain.iec;
      },
      set(value) {
        this.$store.commit(MT.SET_SBMAIN_EXPORTER_IEC, { value });
      }
    },
    address1: {
      get() {
        return this.$store.state.ExportModuleStore.sbMain.exporterAddress1;
      },
      set(value) {
        this.$store.commit(MT.SET_SBMAIN_EXPORTER_ADDRESS1, { value });
      }
    },
    address2: {
      get() {
        return this.$store.state.ExportModuleStore.sbMain.exporterAddress2;
      },
      set(value) {
        this.$store.commit(MT.SET_SBMAIN_EXPORTER_ADDRESS2, { value });
      }
    },
    branch: {
      get() {
        return this.$store.state.ExportModuleStore.sbMain.exporterBranch;
      },
      set(value) {
        this.$store.commit(MT.SET_SBMAIN_EXPORTER_BRANCH, { value });
      }
    },
    city: {
      get() {
        return this.$store.state.ExportModuleStore.sbMain.exporterCity;
      },
      set(value) {
        this.$store.commit(MT.SET_SBMAIN_EXPORTER_CITY, { value: value.text });
      }
    },
    state: {
      get() {
        return this.$store.state.ExportModuleStore.sbMain.exporterState;
      },
      set(value) {
        this.$store.commit(MT.SET_SBMAIN_EXPORTER_STATE, { value });
        this.$store.commit(MT.SET_SBMAIN_EXPORTER_STATE, { value });
      }
    },
    gstnTypesArray: {
      get() {
        const selectedGstnType = this.gstnType;
        if (selectedGstnType !== "" && !!selectedGstnType) {
          return this.gstInTypes.find(type => type.textIdentifier.includes(selectedGstnType));
        } else {
          return this.gstInTypes[0];
        }
      },
      set(value) {
        this.$store.commit(MT.SET_SBMAIN_GSTN_TYPE, { value });
      }
    },
    gstnType: {
      get() {
        return this.$store.state.ExportModuleStore.sbMain.gstnType;
      },
      set(value) {
        this.$store.commit(MT.SET_SBMAIN_GSTN_TYPE, { value });
      }
    },
    gstnId: {
      get() {
        return this.$store.state.ExportModuleStore.sbMain.gstnId;
      },
      set(value) {
        this.$store.commit(MT.SET_SBMAIN_GSTN_ID, { value });
      }
    },
    pin: {
      get() {
        return this.$store.state.ExportModuleStore.sbMain.exporterPin;
      },
      set(value) {
        this.$store.commit(MT.SET_SBMAIN_EXPORTER_PIN, { value });
      }
    },
    authorizedDealerCode: {
      get() {
        const exporterAdCode = this.$store.state.ExportModuleStore.sbMain.exporterAdCode;
        const bankName = this.$store.state.ExportModuleStore.sbMain.exporterBankName;
        if (exporterAdCode !== null && bankName !== null) {
          return exporterAdCode + " - " + bankName;
        }
        return null;
      },
      set(value) {
        this.$store.commit(MT.SET_SBMAIN_EXPORTER_AD_CODE, { value: value.ad_code });
        this.bankName = value.bank_name;
      }
    },
    bankName: {
      get() {
        return this.$store.state.ExportModuleStore.sbMain.exporterBankName;
      },
      set(value) {
        this.$store.commit(MT.SET_SBMAIN_EXPORTER_BANK_NAME, { value: value });
      }
    },
    states() {
      return this.$store.getters.states;
    },
    gstInTypes() {
      return this.$store.getters.gstInTypes;
    },
    cities() {
      return this.$store.getters.cities;
    },
    exporters() {
      return this.$store.getters.exporters;
    },
    breadCrumbItems() {
      return [
        {
          title: "Dashboard",
          route: "dashboard.home",
          children: [
            {
              title: "Export",
              route: "dashboard.export",
              children: [
                {
                  title: "Exporter",
                  route: "dashboard.export.exporter"
                }
              ]
            }
          ]
        }
      ];
    }
  }
};
</script>
